import { CASP } from '@ui/src/components/transactions-sidepanels/types';
import {
  ValidatorsProps,
  PaymentType,
  Account,
  Country,
  Currency,
} from '@ui/src';

export enum BankAndPspItemStatus {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
}

export enum PspPartnerType {
  CREDIT = 'CREDIT',
  PAYMENT = 'PAYMENT',
  EMONEY = 'EMONEY',
  BLOCKCHAIN_CUSTODIAN_EXCHANGE = 'BLOCKCHAIN_CUSTODIAN_EXCHANGE',
}

export enum PspPartnerLocation {
  DOMESTIC = 'DOMESTIC',
  FOREIGN = 'FOREIGN',
}

export enum PspPartnerPaymentType {
  FASTER_PAYMENT = 'FASTER_PAYMENT',
  BACS = 'BACS',
  SEPA_SCT_INST = 'SEPA_SCT_INST',
  SEPA_SCT = 'SEPA_SCT',
  TARGET_2 = 'TARGET_2',
  SWIFT = 'SWIFT',
  CRYPTO_ASSET_PAYMENT = 'CRYPTO_ASSET_PAYMENT',
  FEDWIRE = 'FEDWIRE',
  CASH_OVER_COUNTER = 'CASH_OVER_COUNTER',
}

export enum ConnectionTypeEnum {
  CSM = 'CSM',
  VIRTUAL_IBAN = 'VIRTUAL_IBAN',
  NO_IBAN = 'NO_IBAN',
  PRIME_VAULT = 'PRIME_VAULT',
}

export interface PspIntegrationPayments {
  currencyType: PspAccountCurrencyType;
  id: string;
  connectionsTypes: {
    connectionType: ConnectionTypeEnum;
    id: string;
  }[];
  currencies: {
    currencyId: string;
    id: string;
    currency: {
      name: string;
      id: string;
    };
  }[];
  paymentsTypes: PaymentType[];
}
export interface PspIntegration {
  createdAt: string;
  id: string;
  name: string;
  pspIntegrationPayments: PspIntegrationPayments[];
  externalAccountsAutoCreate?: boolean;
  externalAccountsAutoCreateCurrencies: {
    currencyId: string;
    caspId?: string;
  }[];
  externalAccountsAutoCreateCurrencyType: string;
  externalPspPartnerAccountCreate?: boolean;
  primeVaultCustomersTransactionsFeeTier?: string;
  primeVaultCustomersTransactionsFeesExternalAccountId?: string;
  primeVaultPspPartnersTransactionsFeeTier?: string;
  primeVaultPspPartnersTransactionsFeesExternalAccountId?: string;
  externalCustomersSupported?: boolean;
  hasExchangeRateTolerance?: boolean;
  exchangeRateTolerance?: number;
}

export enum ExportFileTypeEnum {
  HARBOUR_HILLS_B2B_LITE = 'HARBOUR_HILLS_B2B_LITE',
  SPITAMEN = 'SPITAMEN',
  NULL = 'NULL',
}

export enum PspIntegrationAccountStatusEnum {
  IN_PROGRESS = 'IN_PROGRESS',
  FAILED = 'FAILED',
  DONE = 'DONE',
}

export interface PspPartnerItem {
  aba?: string;
  partnerType: PspPartnerType;
  location: PspPartnerLocation;
  paymentsTypes: PspPartnerPaymentType[];
  companyName: string;
  legalName: string;
  swift: string;
  address: string;
  managerContacts: string;
  onlineBankLink: string;
  description: string;
  active: boolean;
  countryId: string;
  pspIntegration?: PspIntegration;
  pspIntegrationId?: string;
  visibleToClients?: boolean;
  sortCode?: string;
  url?: string;
  exportFileType: ExportFileTypeEnum;
  intermediaryBankName?: string;
  intermediaryBankSwift?: string;
  accountOfCorrespondentWithIntermediaryBank?: string;
  caspId?: string;
  casp?: CASP;
  country: {
    name: string;
    numeric: string;
    alpha2: string;
    alpha3: string;
    id: string;
  };
  id: string;
  beneficiary?: string;
  render?: Record<string, React.JSX.Element>;
}

export interface PspPartnerFormValues {
  partnerType: string;
  location: string;
  countryId: string;
  companyName: string;
  legalName: string;
  swift?: string;
  address: string;
  aba?: string;
  managerContacts: string;
  onlineBankLink: string;
  description?: string;
  active: boolean;
  paymentsTypes: PspPartnerPaymentType[];
  pspIntegrationId?: string;
  visibleToClients?: boolean;
  sortCode?: string;
  intermediaryBankName?: string;
  intermediaryBankSwift?: string;
  accountOfCorrespondentWithIntermediaryBank?: string;
  beneficiary?: string;
  exportFileType?: ExportFileTypeEnum | null;
  caspId?: string | null;
}

export interface BankAndPspDocument {
  id: string;
  name: string;
  status: BankAndPspItemStatus;
  link: string;
}

export interface PspPartnersValidator {
  createPspPartnerSchema: ValidatorsProps;
  updatePspPartnerSchema: ValidatorsProps;
}
export interface PspAccountsValidator {
  createPspAccountSchema: ValidatorsProps;
}

export enum PspAccountType {
  SAFEGUARDING = 'SAFEGUARDING',
  SEGGREGATED = 'SEGGREGATED',
  OWN_FUNDS = 'OWN_FUNDS',
}

export enum PspAccountCurrencyType {
  SPECIFIC = 'SPECIFIC',
  ALL = 'ALL',
  NONE = 'NONE',
}

export interface PspAccountValues {
  categoryId: string;
  type?: string;
  currencyType: string;
  currencyId: string[];
  currencies?: Currency[];
  pspPartnerId: string;
  pspPrimaryAccountId?: string;
  description: string;
  visibleToClient: boolean;
  realNumberOrIban?: string;
  accountOfCorrespondentWithIntermediaryBank?: string;
  intermediaryBankName?: string;
  intermediaryBankSwift?: string;
  caspId?: string | null;
  excludeFromTopUpIfExternalAccountAvailable?: boolean;
  externalAccountBindType?: string;
  isDefaultForExternalOperations?: boolean;
  useOwnBankDetails?: boolean;
  bankName?: string | null;
  bankLegalName?: string | null;
  bankCountryId?: string | null;
  bankAddress?: string | null;
  bankSortCode?: string | null;
  bankSwift?: string | null;
  bankAba?: string | null;
}

export interface PspAccountItem extends PspAccountValues {
  id: string;
  active: boolean;
  accountNumber?: string;
  balance?: string;
  url?: string;
  caspId?: string;
  currencies?: Currency[];
  account: {
    id: string;
    fullNumber: string;
    balance: number;
    overdraftEnabled?: boolean;
    totalBalanceBC: number;
  };
  externalAccountId?: string;
  overdraftEnabled?: boolean;
  pspIntegrationAccountStatus?: PspIntegrationAccountStatusEnum;
  render?: Record<string, React.JSX.Element>;
}

export enum PspPartnerOperationType {
  PSP_PARTNER_TYPE = 'PSP_PARTNER_TYPE',
  PSP_LOCATION = 'PSP_LOCATION',
  PSP_PAYMENT_TYPE = 'PSP_PAYMENT_TYPE',
  PSP_COMPANY_NAME = 'PSP_COMPANY_NAME',
  PSP_LEGAL_NAME = 'PSP_LEGAL_NAME',
  PSP_SWIFT = 'PSP_SWIFT',
  PSP_COUNTRY_ID = 'PSP_COUNTRY_ID',
  PSP_ADDRESS = 'PSP_ADDRESS',
  PSP_MANAGER_CONTACTS = 'PSP_MANAGER_CONTACTS',
  PSP_ONLINE_BANK_LINK = 'PSP_ONLINE_BANK_LINK',
  PSP_DESCRIPTION = 'PSP_DESCRIPTION',
  PSP_ACTIVE = 'PSP_ACTIVE',
}

export interface PspPartnerLogs {
  operationType: PspPartnerOperationType;
  ipAddress: string;
  deviceInfo: string;
  userId: string;
  pspPartnerId: string;
  id: string;
  createdAt: string;
  render?: Record<string, React.JSX.Element>;
}

export enum PspAccountLogsType {
  PSPA_TYPE = 'PSPA_TYPE',
  PSPA_ACCOUNT_ID = 'PSPA_ACCOUNT_ID',
  PSPA_CURRENCY_TYPE = 'PSPA_CURRENCY_TYPE',
  PSPA_CURRENCY_ID = 'PSPA_CURRENCY_ID',
  PSPA_DESCRIPTION = 'PSPA_DESCRIPTION',
  PSPA_VISIBLE_TO_CLIENT = 'PSPA_VISIBLE_TO_CLIENT',
}

export interface PspAccountLogs {
  type: PspAccountLogsType;
  value: string;
  pspAccountId: string;
  id: string;
  createdAt: string;
  render?: Record<string, React.JSX.Element>;
}

export interface Balance {
  accountId: string;
  currencyId: string;
  id: string;
  value: number;
  currency: Currency;
  overdraftEnabled?: boolean;
  overdraftValue?: string;
}

export interface UploadResponse {
  userId: string;
  mimeType: string;
  size: number;
  id: string;
  createdAt: string;
  name: string;
}

export enum DocumentStatus {
  NEW = 'NEW',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
}

export interface Document {
  name: string;
  uploadId: string;
  category: string;
  expirationDate: string;
  status: DocumentStatus;
  userId: string;
  id: string;
  createdAt: string;
}

export interface PaymentsSettings {
  bic: string;
  createdAt: string;
  id: string;
  pspIntegrationPaymentId: string;
  pspPartnerId: string;
  sortCode: string;
}

export enum CurrencyType {
  ALL = 'ALL',
  NONE = 'NONE',
  SPECIFIC = 'SPECIFIC',
}

export enum PspPartnerTypeEnum {
  CREDIT = 'CREDIT',
  PAYMENT = 'PAYMENT',
  EMONEY = 'EMONEY',
}

export enum PspPartnerLocationEnum {
  DOMESTIC = 'DOMESTIC',
  FOREIGN = 'FOREIGN',
}

export interface ConnectionType {
  pspIntegrationPaymentId: string;
  connectionType: ConnectionTypeEnum;
  id: string;
}

export interface PspIntergrationCurrency {
  pspIntegrationPaymentId: string;
  currencyId: string;
  currency: Currency;
  id: string;
}

export interface PspPartner {
  partnerType: PspPartnerTypeEnum;
  location: PspPartnerLocationEnum;
  paymentsTypes: string[];
  companyName: string;
  legalName: string;
  swift?: string;
  sortCode?: string;
  pspIntegrationId?: string;
  pspIntegration?: {
    name: string;
    pspIntegrationPayments: PspIntegrationPayment[];
    id: string;
  };
  address: string;
  managerContacts?: string;
  onlineBankLink?: string;
  description?: string;
  active: boolean;
  visibleToClients: boolean;
  countryId: string;
  country: Country;
  intermediaryBankName?: string;
  intermediaryBankSwift?: string;
  accountOfCorrespondentWithIntermediaryBank?: string;
  beneficiary?: string;
  exportFileType?: ExportFileTypeEnum;
  id: string;
}

export interface PaymentsPartnersSetting {
  pspIntegrationPaymentId: string;
  pspIntegrationPayment: PspIntegrationPayment;
  pspPartnerId: string;
  pspPartner: PspPartner;
  bic?: string;
  sortCode?: string;
  id: string;
}

export interface PspIntegrationPayment {
  pspIntegrationId: string;
  paymentsTypes: string[];
  currencyType: CurrencyType;
  connectionsTypes: ConnectionType[];
  currencies: PspIntergrationCurrency[];
  paymentsPartnersSettings: PaymentsPartnersSetting[];
  id: string;
}

export interface ExternalAccount {
  account: Account & { organization: { type: string } };
  accountId?: string;
  iban: string;
  id: string;
  pspIntegrationConnectionId: string;
  pspIntegrationPayment?: PspIntegrationPayment;
  pspIntegrationPaymentId?: string;
  pspPartner?: PspPartner;
  bank?: string;
  caspId?: string;
  currencyId?: string;
  paymentType?: string;
  accountNumber?: string;
  accountName?: string;
  accountType?: string;
  active?: boolean;
  createdAt?: string;
  createType?: string;
  currenciesIds?: string[];
  pspIntegrationPaymentAccountsCount?: number;
  render?: Record<string, React.JSX.Element>;
}

export interface ExternalAccountFormValues {
  pspPartnerId: string;
  accountId?: string;
  iban: string;
  paymentType: string;
  currencyId: string[];
  status?: string;
  active: boolean;
  caspId?: string;
  currenciesIds: string[];
}

export interface CASPChainFormValues {
  caspId: string;
  chain: string;
  pspIntegrationId: string;
  pspPartnerId: string;
}

export interface CreateDocumentTypes {
  accountPage?: boolean;
}

export interface IntegrationLog {
  connectionType: string;
  createdAt: string;
  deletedAt?: string;
  eventType?: string;
  id: string;
  pspPartner: PspPartner;
  pspPartnerId: string;
  requestTime?: string;
  responseBody?: string;
  requestBody?: string;
  responseTime?: string;
  statusCode: number;
  type: string;
  types: string;
  updatedAt?: string;
  url?: string;
  urlEventType?: string;
  transactionId?: string;
  render?: Record<string, React.JSX.Element>;
}

export interface ExternalAccountWithIntegrationFormValues {
  accountsIds: string[];
  integrationId: string;
  currenciesIds: string[];
  caspId?: string;
}

export interface ExternalVaultSettings {
  externalAccountsAutoCreate: boolean;
  externalPspPartnerAccountCreate: boolean;
  externalAccountsAutoCreateCurrencyType: string;
  externalAccountsAutoCreateCurrencies:
    | {
        currencyId: string;
        caspId?: string;
        id?: string;
      }[]
    | null;
}

export interface ExternalAccountCurrenciesSettings {
  externalAccountsAutoCreateCurrencyType: string | null;
  externalAccountsAutoCreateCurrencies?:
    | {
        currencyId: string;
        caspId?: string;
        id?: string;
      }[]
    | null;
}

export enum FeeTierEnum {
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
}

export interface FeeSettings {
  primeVaultCustomersTransactionsFeeTier: FeeTierEnum;
  primeVaultPspPartnersTransactionsFeeTier: FeeTierEnum;
  primeVaultCustomersTransactionsFeesExternalAccountId: string | null;
  primeVaultPspPartnersTransactionsFeesExternalAccountId: string | null;
}

export interface ExternalAccountPageTypes {
  accountPage?: boolean;
}

export interface ExternalAccountTopUpOption {
  accountNumber: string;
  bankAccountHolderName: string;
  bankAddress: string;
  bankCountry: string;
  bankName: string;
  bic: string;
  createdAt: string;
  currencyId: string;
  iban: string;
  id: string;
  paymentType?: string;
  pspIntegrationAccountId: string;
  routingCodeEntries?: {
    id?: string;
    routingCodeKey: string;
    routingCodeValue: string;
  }[];
  thirdPartyIntegrationId?: string;
  thirdPartyReference?: string;
}

export interface PspAccountCategory {
  active: boolean;
  id: string;
  name: string;
  primaryAccountId?: string;
  type?: string;
}

export interface TransactionFeeSettings {
  id: string;
  pspIntegrationId: string;
  feeTier: string;
  caspId: string;
  transactionTypeId: string;
  feeIntegrationAccountId?: string;
}

export interface TransactionFeeSettingsFormValues {
  caspId: string;
  feeTier: FeeTierEnum;
  feeIntegrationAccountId?: string;
  transactionTypeId?: string;
}

export interface PspPartnersOrganization {
  id: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  type: string;
}

export interface PspPartnersLinkedClient {
  active: boolean;
  createdAt: string;
  externalId: string;
  id: string;
  organizationId: string;
  organization: {
    firstName?: string;
    lastName?: string;
    companyName?: string;
    id: string;
  };
  organizationType: string;
  pspPartnerId?: string;
  render?: Record<string, React.JSX.Element>;
}

export interface LinkedClientsFormValues {
  organizationId: string;
  organizationType: string;
  externalId: string;
  active: boolean;
  pspPartnerId: string;
}
export interface PspIntegrationOrganization {
  externalId: string;
  name: string;
  organizationType: string;
}

export interface ExternalAccountTopUpOptionFormValues {
  id?: string;
  paymentType: string;
  currencyId: string;
  bankAccountHolderName: string;
  accountNumber: string;
  bankAddress: string;
  bic: string;
  iban: string;
  bankName: string;
  thirdPartyReference?: string;
  pspIntegrationAccountId?: string;
  routingCodeEntries?:
    | {
        id?: string;
        routingCodeKey: string;
        routingCodeValue: string;
      }[]
    | null;
}

export enum RoutingCodeKeyEnum {
  ABA = 'ABA',
  BLZ_CODE = 'BLZ_CODE',
  BRANCH_CODE = 'BRANCH_CODE',
  BSB_CODE = 'BSB_CODE',
  SORT_CODE = 'SORT_CODE',
  IFS_CODE = 'IFS_CODE',
  CLABE_CODE = 'CLABE_CODE',
  CTN = 'CTN',
}
